/**
 * ClubCard Component
 * 
 * Displays club information with notification indicators for homepage "My Clubs" section.
 * Features enhanced club metadata, notification badges, and smart visual hierarchy.
 * 
 * Issue #409: Convert "Today's Activities" to "My Clubs" implementation
 * Design: ORO Nightfall semantic token system (Layer 2)
 */

import { useState } from 'react';
import { Users, MapPin, Bell, Calendar, MessageCircle } from 'lucide-react';
import type { ClubWithRole } from '../lib/api/clubs';

interface ClubCardProps {
  club: ClubWithRole & {
    unread_announcements?: number;
    upcoming_events_this_week?: number;
    unread_messages?: number;
    recent_activity?: boolean;
    avatar_url?: string;
  };
  onClick?: () => void;
}

/**
 * Simple image component with fallback for club avatars
 */
function ClubAvatarWithFallback({ 
  src, 
  alt, 
  className,
  clubName 
}: { 
  src?: string | null; 
  alt: string; 
  className?: string;
  clubName: string;
}) {
  const [error, setError] = useState(false);

  if (error || !src) {
    // Generate initials from club name
    const initials = clubName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();

    return (
      <div 
        className={`flex items-center justify-center font-medium ${className}`}
        style={{
          backgroundColor: 'var(--oro-surface-alt)',
          color: 'var(--text-secondary)'
        }}
      >
        {initials}
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
    />
  );
}

export default function ClubCard({ club, onClick }: ClubCardProps) {
  const hasNotifications = (club.unread_announcements || 0) > 0;
  const hasUpcomingEvents = (club.upcoming_events_this_week || 0) > 0;
  const hasUnreadMessages = (club.unread_messages || 0) > 0;

  return (
    <div
      className="relative p-4 rounded-2xl cursor-pointer transition-all duration-200"
      style={{
        backgroundColor: 'var(--oro-surface)',
        border: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-card-soft)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-border-hover)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-border)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {/* Club Avatar */}
        <div className="relative">
          <ClubAvatarWithFallback
            src={club.avatar_image_url || club.hero_image_url}
            alt={`${club.name} avatar`}
            clubName={club.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        </div>

        {/* Club Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
              {club.name}
            </h3>
            
            {/* Role badge for admins */}
            {club.role === 'owner' && (
              <span 
                className="px-1.5 py-0.5 text-xs rounded font-medium"
                style={{
                  backgroundColor: 'var(--oro-gold)',
                  color: 'var(--oro-bg)'
                }}
              >
                Owner
              </span>
            )}
            {club.role === 'leader' && (
              <span 
                className="px-1.5 py-0.5 text-xs rounded font-medium"
                style={{
                  backgroundColor: 'rgba(225, 195, 122, 0.2)',
                  color: 'var(--oro-gold)'
                }}
              >
                Leader
              </span>
            )}
          </div>

          {/* Club metadata */}
          <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {/* Member count */}
            <div className="flex items-center gap-1">
              <Users className="oro-icon-muted" />
              <span>{club.member_count}</span>
            </div>

            {/* Location */}
            {club.location && (
              <div className="flex items-center gap-1">
                <MapPin className="oro-icon-muted" />
                <span className="truncate">{club.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notification indicators */}
        <div className="flex flex-col items-end gap-1">
          {/* Upcoming events badge - using oro-success semantic token */}
          {hasUpcomingEvents && (
            <div 
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: 'var(--oro-success-soft)',
                color: 'var(--oro-success)'
              }}
            >
              <Calendar className="oro-icon-success" />
              <span>{club.upcoming_events_this_week}</span>
            </div>
          )}

          {/* Unread chat messages badge - using oro-accent (gold) semantic token */}
          {hasUnreadMessages && (
            <div 
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: 'rgba(225, 195, 122, 0.12)',
                color: 'var(--oro-gold)'
              }}
            >
              <MessageCircle className="oro-icon-accent" />
              <span>{club.unread_messages}</span>
            </div>
          )}

          {/* Unread announcements indicator - using oro-warning semantic token */}
          {hasNotifications && (
            <div 
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: 'var(--oro-warning-soft)',
                color: 'var(--oro-warning)'
              }}
            >
              <Bell className="oro-icon-alert" />
              <span>{club.unread_announcements}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}